export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_STORAGE_KEY = `pt_cookie_consent_v${COOKIE_CONSENT_VERSION}`;
export const COOKIE_CONSENT_EVENT = 'pt_cookie_consent_changed';
export const APP_SETTINGS_COOKIE_CONSENT_KEY = 'cookieConsent';

/**
 * Consent shape:
 * {
 *   v: number,
 *   updatedAt: string (ISO),
 *   choices: { necessary: true, analytics: boolean, marketing: boolean }
 * }
 */

function normalizeCookieConsent(raw) {
  if (!raw || raw.v !== COOKIE_CONSENT_VERSION || !raw.choices) return null;
  return {
    v: COOKIE_CONSENT_VERSION,
    updatedAt: raw.updatedAt || new Date().toISOString(),
    choices: {
      necessary: true,
      analytics: !!raw.choices.analytics,
      marketing: !!raw.choices.marketing,
    },
  };
}

export function getCookieConsentFromAppSettings(appSettings) {
  if (!appSettings) return null;
  return normalizeCookieConsent(appSettings[APP_SETTINGS_COOKIE_CONSENT_KEY]);
}

export function getStoredCookieConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return normalizeCookieConsent(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

function writeLocalCookieConsent(consent) {
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch (e) {
    // Ignore storage failures (e.g. private mode)
  }
}

export function getEffectiveCookieConsent(appSettings) {
  const fromSettings = getCookieConsentFromAppSettings(appSettings);
  const fromLocal = getStoredCookieConsent();

  if (fromSettings) {
    if (JSON.stringify(fromSettings) !== JSON.stringify(fromLocal)) {
      writeLocalCookieConsent(fromSettings);
    }
    return fromSettings;
  }

  return fromLocal;
}

export function storeCookieConsent(choices) {
  const consent = normalizeCookieConsent({
    v: COOKIE_CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    choices: {
      necessary: true,
      analytics: !!choices?.analytics,
      marketing: !!choices?.marketing,
    },
  });

  writeLocalCookieConsent(consent);

  try {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: consent }));
  } catch (e) {
    // Ignore if CustomEvent not available
  }

  return consent;
}

/**
 * Account settings replace the whole app_settings blob on save.
 * Sync only after that blob has loaded; a missing object is the login
 * gap (token is set, settings are not) and must not be written as {}.
 */
export function canSyncCookieConsent({ token, isInitializing, appSettings, remoteConsentKey } = {}) {
  if (!token || isInitializing) return false;
  if (appSettings == null || typeof appSettings !== 'object') return false;
  if (remoteConsentKey) return false;
  return true;
}

export async function syncCookieConsentToServer({
  token,
  appSettings,
  updateAppSettings,
} = {}) {
  const remote = getCookieConsentFromAppSettings(appSettings);
  if (remote) return remote;

  const local = getStoredCookieConsent();
  if (!local || !token || !updateAppSettings) return local;
  if (appSettings == null || typeof appSettings !== 'object') return local;

  const existingSerialized = JSON.stringify(getCookieConsentFromAppSettings(appSettings));
  const localSerialized = JSON.stringify(local);
  if (existingSerialized === localSerialized) return local;

  try {
    await updateAppSettings(token, { [APP_SETTINGS_COOKIE_CONSENT_KEY]: local });
  } catch (e) {
    console.error('Failed to sync cookie consent to server:', e);
  }

  return local;
}

export async function persistCookieConsent(choices, {
  token,
  appSettings,
  updateAppSettings,
} = {}) {
  const consent = storeCookieConsent(choices);

  if (!token || !updateAppSettings) return consent;
  if (appSettings == null || typeof appSettings !== 'object') return consent;

  try {
    await updateAppSettings(token, { [APP_SETTINGS_COOKIE_CONSENT_KEY]: consent });
  } catch (e) {
    console.error('Failed to save cookie consent to server:', e);
  }

  return consent;
}

export function hasAnalyticsConsent(consent = null) {
  const c = consent ?? getStoredCookieConsent();
  return c?.choices?.analytics === true;
}

export function hasMarketingConsent(consent = null) {
  const c = consent ?? getStoredCookieConsent();
  return c?.choices?.marketing === true;
}
